/**
 * variant-gallery-filter.js
 *
 * Filters the Prestige product gallery so only the images associated with the
 * currently selected variant are shown.
 *
 * Logic (position-range, not alt-tag):
 *   - Each variant has a featured_media.position (1-based).
 *   - The variant's "range" of media = positions from its featured_media.position
 *     up to (but not including) the next variant's featured_media.position.
 *   - Variants without a featured_media → no filtering (show all).
 *
 * The variant→position map is rendered inline by product-gallery.liquid as
 *   <script type="application/json" data-variant-media-positions>
 *     [{"id":<variant_id>,"pos":<media_position_or_null>}, ...]
 *   </script>
 */

(function () {
  'use strict';

  /**
   * Parse the variant→position map embedded in the gallery.
   * Returns { variantId: featuredMediaPosition_or_null }.
   */
  function readVariantPositions(gallery) {
    const script = gallery.querySelector('script[data-variant-media-positions]');
    if (!script) return null;
    try {
      const list = JSON.parse(script.textContent);
      const map = {};
      list.forEach(function (v) { map[v.id] = v.pos; });
      return { byId: map, list: list };
    } catch (e) {
      return null;
    }
  }

  /**
   * Sorted unique featured_media positions across all variants.
   */
  function uniqueSortedPositions(variantList) {
    const set = new Set();
    variantList.forEach(function (v) { if (v.pos != null) set.add(v.pos); });
    return Array.from(set).sort(function (a, b) { return a - b; });
  }

  /**
   * Range [start, end] (both inclusive, 1-based positions) for the given variant.
   * If the variant has no featured_media, returns null → no filtering.
   * The end is one less than the next unique featured_media position; for the
   * last variant, the end is +Infinity (show through end of gallery).
   */
  function computeRange(variant, info) {
    if (!variant) return null;
    const start = info.byId[variant.id];
    if (start == null) return null;
    const positions = uniqueSortedPositions(info.list);
    const idx = positions.indexOf(start);
    const end = (idx >= 0 && idx + 1 < positions.length) ? positions[idx + 1] - 1 : Infinity;
    return { start: start, end: end };
  }

  /**
   * 0-based indexes of media cells that should be hidden for the given range.
   */
  function indexesOutsideRange(range, cells) {
    if (!range) return [];
    const hidden = [];
    cells.forEach(function (cell, index) {
      const pos = parseInt(cell.getAttribute('data-media-position'), 10);
      if (!pos) return;
      if (pos < range.start || pos > range.end) hidden.push(index);
    });
    return hidden;
  }

  function applyFilter(gallery, variant, info) {
    if (!info) return;

    const carousel = gallery.querySelector('scroll-carousel');
    if (!carousel || typeof carousel.filter !== 'function') return;

    const allCells = Array.from(gallery.querySelectorAll('.product-gallery__media'));
    const range = computeRange(variant, info);
    const hiddenIndexes = indexesOutsideRange(range, allCells);
    const hiddenPositions = hiddenIndexes.map(function (i) { return i + 1; });

    gallery.setAttribute('filtered-indexes', JSON.stringify(hiddenPositions));
    carousel.filter(hiddenIndexes);

    // Sync thumbnail buttons and dot buttons sitting outside the carousel
    const auxBtns = gallery.querySelectorAll(
      '.product-gallery__thumbnail, .page-dots .tap-area'
    );
    auxBtns.forEach(function (btn) {
      const pos = parseInt(btn.getAttribute('data-media-position'), 10);
      if (!pos) return;
      btn.toggleAttribute('hidden', range != null && (pos < range.start || pos > range.end));
    });

    // Scroll to the variant's featured media if it's still visible
    if (variant && variant.featured_media) {
      const target = gallery.querySelector('[data-media-id="' + variant.featured_media.id + '"]');
      if (target && !target.hasAttribute('hidden')) {
        if (carousel.isScrollable) {
          const visible = Array.from(allCells).filter(function (c) { return !c.hasAttribute('hidden'); });
          const vi = visible.indexOf(target);
          if (vi >= 0 && typeof carousel.select === 'function') {
            carousel.select(vi, { instant: true });
          }
        } else {
          target.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      }
    }
  }

  function attachFilter(gallery) {
    const formId = gallery.getAttribute('form');
    if (!formId) return;

    const form = document.getElementById(formId) || document.querySelector('[id="' + formId + '"]');
    if (!form) return;

    const info = readVariantPositions(gallery);
    if (!info) return;

    // Apply once on init so initial render matches the selected variant
    const initialScript = document.querySelector('script[data-variant][type="application/json"]');
    if (initialScript) {
      try {
        const initialVariant = JSON.parse(initialScript.textContent);
        applyFilter(gallery, initialVariant, info);
      } catch (e) { /* noop */ }
    }

    form.addEventListener('variant:change', function (event) {
      const variant = event.detail && event.detail.variant;
      if (!variant) return;
      applyFilter(gallery, variant, info);
    });
  }

  function init() {
    document.querySelectorAll('product-gallery').forEach(attachFilter);

    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.tagName === 'PRODUCT-GALLERY') attachFilter(node);
          node.querySelectorAll &&
            node.querySelectorAll('product-gallery').forEach(attachFilter);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
